import React, { useMemo } from 'react';
import { Flex, IconButton, Text, Toggle } from '@vibe/core';
import {
  MoveArrowLeft,
  MoveArrowRight,
  Download,
  Collapse,
  Expand,
} from '@vibe/icons';
import { format, parseISO } from 'date-fns';
import ProgressExplanation from './ProgressExplanation';
import TimesheetPeriodPicker from './TimesheetPeriodPicker';
import type {
  TimesheetGroupBy,
  TimesheetDateRange,
  TimesheetViewMode,
  TimesheetPeriod,
  ProgressViewType,
} from '../../types/timesheet.types';

export interface TimesheetTopBarProps {
  groupBy: TimesheetGroupBy;
  period: TimesheetPeriod;
  dateRange: TimesheetDateRange;
  viewMode: TimesheetViewMode;
  viewLabel: string;
  onPeriodChange: (period: TimesheetPeriod) => void;
  onCustomRangeChange: (start: string, end: string) => void;
  onDatePrev: () => void;
  onDateNext: () => void;
  onToday: () => void;
  onExport: () => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  hasExpandableRows: boolean;
  subGroupBy: 'category' | null;
  onSubGroupByChange: (value: 'category' | null) => void;
  /** Optional content rendered at the right end of the bar (e.g., submit button). */
  rightSlot?: React.ReactNode;
}

const TimesheetTopBar: React.FC<TimesheetTopBarProps> = ({
  groupBy,
  period,
  dateRange,
  viewMode,
  viewLabel,
  onPeriodChange,
  onCustomRangeChange,
  onDatePrev,
  onDateNext,
  onToday,
  onExport,
  onExpandAll,
  onCollapseAll,
  hasExpandableRows,
  subGroupBy,
  onSubGroupByChange,
  rightSlot,
}) => {
  const dateRangeLabel = useMemo(() => {
    if (!dateRange.start || !dateRange.end) return '';
    const startDate = parseISO(dateRange.start);
    const endDate = parseISO(dateRange.end);
    return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
  }, [dateRange]);

  return (
    <div className="rp-ts-topbar">
      <Flex
        gap={Flex.gaps.SMALL}
        align={Flex.align.CENTER}
        className="rp-ts-topbar__left"
      >
        {/* Title */}
        <Text type={Text.types.TEXT1} weight={Text.weights.BOLD}>
          {viewLabel}
        </Text>

        {/* Period picker */}
        <TimesheetPeriodPicker
          period={period}
          dateRange={dateRange}
          onPeriodChange={onPeriodChange}
          onCustomRangeChange={onCustomRangeChange}
        />
      </Flex>

      <Flex
        gap={Flex.gaps.SMALL}
        align={Flex.align.CENTER}
        className="rp-ts-topbar__center"
      >
        {/* Date navigation */}
        <IconButton
          icon={MoveArrowLeft}
          kind={IconButton.kinds.TERTIARY}
          size={IconButton.sizes.SMALL}
          onClick={onDatePrev}
          ariaLabel="Previous period"
        />

        <button className="rp-ts-topbar__date-label" onClick={onToday}>
          {dateRangeLabel}
        </button>

        <IconButton
          icon={MoveArrowRight}
          kind={IconButton.kinds.TERTIARY}
          size={IconButton.sizes.SMALL}
          onClick={onDateNext}
          ariaLabel="Next period"
        />
      </Flex>

      <Flex
        gap={Flex.gaps.XS}
        align={Flex.align.CENTER}
        className="rp-ts-topbar__right"
      >
        {/* Explanation button (progress views only) */}
        {viewMode === 'progress' && (
          <ProgressExplanation
            progressType={groupBy as ProgressViewType}
          />
        )}

        {/* Sub-group by Category toggle (timeline only) */}
        {viewMode === 'timeline' && (
          <Flex gap={Flex.gaps.XS} align={Flex.align.CENTER}>
            <Text type={Text.types.TEXT2}>Sub-group by Category</Text>
            <Toggle
              isSelected={subGroupBy === 'category'}
              onChange={(checked: boolean) =>
                onSubGroupByChange(checked ? 'category' : null)
              }
              size="small"
              ariaLabel="Sub-group by Category"
            />
          </Flex>
        )}

        {/* Expand/Collapse (only for hierarchical timeline modes) */}
        {viewMode === 'timeline' && hasExpandableRows && (
          <>
            <IconButton
              icon={Expand}
              kind={IconButton.kinds.TERTIARY}
              size={IconButton.sizes.SMALL}
              onClick={onExpandAll}
              ariaLabel="Expand all"
            />
            <IconButton
              icon={Collapse}
              kind={IconButton.kinds.TERTIARY}
              size={IconButton.sizes.SMALL}
              onClick={onCollapseAll}
              ariaLabel="Collapse all"
            />
          </>
        )}

        {/* Export */}
        <IconButton
          icon={Download}
          kind={IconButton.kinds.TERTIARY}
          size={IconButton.sizes.SMALL}
          onClick={onExport}
          ariaLabel="Export to Excel"
        />

        {/* Right slot (e.g., submit button) */}
        {rightSlot}
      </Flex>
    </div>
  );
};

export default TimesheetTopBar;
